import React, { useState } from "react";
import { Mail, ArrowRight } from "lucide-react";
import { supabase } from "../config/supabase";
import {
  canResendNow,
  resendSuccessMessage,
  sanitizeResendError,
  secondsUntilResend,
} from "../utils/confirmationResend";

interface EmailConfirmationScreenProps {
  isOpen: boolean;
  userEmail: string;
  onClose: () => void;
}

const EmailConfirmationScreen: React.FC<EmailConfirmationScreenProps> = ({
  isOpen,
  userEmail,
}) => {
  const [isResending, setIsResending] = useState(false);
  const [lastResendAt, setLastResendAt] = useState<number | null>(null);
  const [status, setStatus] = useState<{ kind: 'idle' | 'success' | 'error'; text: string }>({
    kind: 'idle',
    text: '',
  });

  if (!isOpen) return null;

  const handleResend = async () => {
    if (!canResendNow(lastResendAt)) {
      setStatus({
        kind: 'error',
        text: `Please wait ${secondsUntilResend(lastResendAt)} seconds before requesting another email.`,
      });
      return;
    }

    setIsResending(true);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: userEmail,
      });
      setLastResendAt(Date.now());
      if (error) {
        setStatus({ kind: 'error', text: sanitizeResendError(error.message) });
      } else {
        setStatus({ kind: 'success', text: resendSuccessMessage() });
      }
    } catch (error) {
      setLastResendAt(Date.now());
      setStatus({
        kind: 'error',
        text: sanitizeResendError(error instanceof Error ? error.message : ''),
      });
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
    >
      <div 
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto relative"
      >

        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-blue-600 p-6 text-white text-center">
          <div className="w-20 h-20 sm:w-24 sm:h-24 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
            <Mail className="w-10 h-10 sm:w-12 sm:h-12" />
          </div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2 sm:mb-3">
            Confirm Your Email
          </h1>
          <p className="text-blue-100 text-sm sm:text-base md:text-lg">
            One quick step before you begin
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 sm:p-8">
          <div className="max-w-lg mx-auto space-y-6 sm:space-y-8">
            <div className="flex justify-center">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-blue-100 rounded-full flex items-center justify-center">
                <Mail className="w-8 h-8 sm:w-10 sm:h-10 text-blue-600" />
              </div>
            </div>

            <div className="text-center space-y-3 sm:space-y-4">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
                One Last Step: Confirm Your Email
              </h2>
              <p className="text-base sm:text-lg text-gray-700">
                We've sent a confirmation email to:
              </p>
              <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-3 sm:p-4">
                <p className="font-semibold text-blue-900 text-base sm:text-lg break-all">
                  {userEmail}
                </p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-4 sm:p-6 border-2 border-green-200">
              <h3 className="font-bold text-gray-900 mb-3 sm:mb-4 flex items-center justify-center text-base sm:text-lg">
                <ArrowRight className="w-5 h-5 mr-2 text-green-600" />
                What Happens Next
              </h3>
              <ol className="space-y-3 sm:space-y-4 text-sm sm:text-base text-gray-700">
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-6 h-6 sm:w-7 sm:h-7 bg-green-500 text-white rounded-full flex items-center justify-center font-bold text-sm mr-3 mt-0.5">
                    1
                  </span>
                  <span>Check your email inbox (and spam folder)</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-6 h-6 sm:w-7 sm:h-7 bg-green-500 text-white rounded-full flex items-center justify-center font-bold text-sm mr-3 mt-0.5">
                    2
                  </span>
                  <span>Click the confirmation link in the email</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-6 h-6 sm:w-7 sm:h-7 bg-green-500 text-white rounded-full flex items-center justify-center font-bold text-sm mr-3 mt-0.5">
                    3
                  </span>
                  <span>Complete your golf profile quiz (12 quick questions)</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-6 h-6 sm:w-7 sm:h-7 bg-green-500 text-white rounded-full flex items-center justify-center font-bold text-sm mr-3 mt-0.5">
                    4
                  </span>
                  <span>Start using ClubIQ with your personalized recommendations!</span>
                </li>
              </ol>
            </div>

            <div className="text-center">
              <p className="text-xs sm:text-sm text-gray-500">
                Didn't receive the email?{" "}
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={isResending || !canResendNow(lastResendAt)}
                  className="text-blue-600 hover:text-blue-700 font-medium underline disabled:text-gray-400 disabled:no-underline"
                >
                  {isResending ? 'Sending…' : canResendNow(lastResendAt) ? 'Resend confirmation' : `Retry in ${secondsUntilResend(lastResendAt)}s`}
                </button>
              </p>
              {status.kind !== 'idle' && (
                <p className={`mt-2 text-sm ${status.kind === 'success' ? 'text-green-700' : 'text-gray-700'}`}>
                  {status.text}
                </p>
              )}
            </div>
            
            <div className="text-center p-4 bg-yellow-50 border-2 border-yellow-200 rounded-lg">
              <p className="text-sm font-semibold text-gray-800">
                Email confirmation is required to access ClubIQ
              </p>
              <p className="text-xs text-gray-600 mt-2">
                Please check your email and click the confirmation link to continue
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailConfirmationScreen;
